<template>
  <RevForm
    :id="FORM_ID"
    :initial-values
    :name="FORM_ID"
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ values, errors, hasError }">
      <RevDrawer
        :name="MODAL_NAME"
        size="large"
        :title="i18n(translations.modalTitle)"
      >
        <template #body>
          <RevLoadingScreen
            v-if="isSubmitting"
            class="mt-32"
            :text="i18n(translations.loadingText)"
          />
          <div
            v-if="!isSubmitting"
            class="relative flex-1 space-y-24 px-0 py-16 md:space-y-56 md:p-0"
          >
            <div>
              <h4 class="body-1-bold mb-12">
                {{ i18n(translations.legalAreaFieldSetLabel) }}
              </h4>
              <div class="grid grid-cols-2 gap-8">
                <RevPicker
                  :class="{ 'border-action-danger-hi': errors.legalArea }"
                  data-test="euLegalAreaPicker"
                  :label="i18n(translations.euLegalArealPickerLabel)"
                  :model-value="isEuArea(values)"
                  @update:model-value="handleChangeLegalArea(LEGAL_AREAS.EU)"
                />
                <RevPicker
                  :class="{ 'border-action-danger-hi': errors.legalArea }"
                  data-test="notEuLegalAreaPicker"
                  :label="i18n(translations.notEuLegalArealPickerLabel)"
                  :model-value="isNonEuArea(values)"
                  @update:model-value="
                    handleChangeLegalArea(LEGAL_AREAS.NON_EU)
                  "
                />
              </div>
              <p
                v-if="errors.legalArea"
                class="text-action-default-low body-2 mt-8 flex items-baseline peer-disabled:text-action-default-low-disabled"
                :class="{ 'text-action-danger-hi': errors.legalArea }"
              >
                {{ errors.legalArea }}
              </p>
            </div>
            <component
              :is="getFormFields(values)"
              :errors
              :expedition-country-options
              :initial-values="values"
              :should-show-exempt-checkbox="isEuArea(values)"
            />
          </div>
        </template>
        <template #footer>
          <div class="grid sm:grid-cols-1 md:grid-cols-2 md:gap-8">
            <RevButton
              id="publish_tax_details_button"
              :disabled="hasError"
              :loading="isSubmitting"
              variant="primary"
              @click.prevent="handleAddInformations"
            >
              {{ i18n(translations.addTaxDetails) }}
            </RevButton>
            <RevButton
              id="save_draft_tax_details_button"
              :disabled="hasError"
              :loading="isSubmitting"
              variant="secondary"
              @click.prevent="handleSaveDraft"
            >
              {{ i18n(translations.saveAsDraftTaxDetails) }}
            </RevButton>
          </div>
        </template>
      </RevDrawer>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'

import { type Country, sellerProfileAPI } from '@backmarket/http-api'
import type {
  GetLegalDocuments,
  GetVatInformations,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import {
  type FieldName,
  RevForm,
  type ValidateMethod,
  makeValidate,
} from '@ds/components/Form'
import { setFormValues, submitForm } from '@ds/components/Form/Form.actions'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'
import { RevPicker } from '@ds/components/Picker'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import {
  deleteLegalDocument,
  uploadLegalDocument,
} from '~/scopes/profile/pages/Profile.utils'

import { FORM_IDS, MODAL_NAMES } from '../../../../../../Profile.constants'
import { LEGAL_AREAS } from '../../TaxDetails.constants'
import type { LegalArea } from '../../TaxDetails.types'

import { OSS_DOCUMENT_TYPE } from './TaxDetailsModal.constants'
import translations from './TaxDetailsModal.translations'
import type { FormTaxValues } from './TaxDetailsModal.types'
import {
  cleanFormValues,
  getFormValidationRules,
} from './TaxDetailsModal.utils'
import EuFormfields from './components/EuFormFields/EuFormFields.vue'
import NonEuFormfields from './components/NonEuFormFields/NonEuFormFields.vue'

const MODAL_NAME = MODAL_NAMES.EDIT_TAX_DETAILS
const FORM_ID = FORM_IDS.EDIT_TAX_DETAILS

const props = defineProps<{
  expeditionCountries: Country[]
  vatInformations: GetVatInformations | null | undefined
  ossDocuments: GetLegalDocuments | null
}>()

const emit = defineEmits(['update-vat-informations'])

const i18n = useI18n()
const { id: sellerId } = useSellerInfoStore()
const { openSuccessToast, openErrorToast } = useTheToast()

const cancelUploadFilePoll = ref<() => void>()

onUnmounted(() => {
  if (cancelUploadFilePoll.value) {
    cancelUploadFilePoll.value()
  }
})

const isSubmitting = ref(false)

// Required fields to have in the payload
//   legalArea
//   hasEstablishmentInEu
//   intraCommunityVatNumber
//   taxIdNumber
//   vatMarginScheme
//   isOssRegistered
//   hasCompletedVatInfos
//   vatNumbersByCountry
const initialValues = computed<FormTaxValues>(
  () =>
    ({
      ...props.vatInformations,
      // Cast values from API
      files: [],
      // Here is true by default to trigger validation rules
      hasCompletedVatInfos: true,
      hasEstablishmentInEu: !!props.vatInformations?.hasEstablishmentInEu,
      intraCommunityVatNumber:
        props.vatInformations?.intraCommunityVatNumber || '',
      isExemptFromVat: !!props.vatInformations?.isExemptFromVat,
      isOssRegistered: props.vatInformations?.isOssRegistered,
      ossDocuments: props.ossDocuments || [],
      taxIdNumber: props.vatInformations?.taxIdNumber || '',
      vatMarginScheme: props.vatInformations?.vatMarginScheme || null,
      vatNumbersByCountry: props.vatInformations?.vatNumbersByCountry || [],
    }) as FormTaxValues,
)

const expeditionCountryOptions = computed(() =>
  props.expeditionCountries.map((countryCode) => ({
    label: i18n.country(countryCode),
    value: countryCode,
  })),
)

function isEuArea({ legalArea }: FormTaxValues) {
  return legalArea === LEGAL_AREAS.EU
}

// Important because apparently legalArea can be null (cf Pastrami tests)
function isNonEuArea({ legalArea }: FormTaxValues) {
  return legalArea === LEGAL_AREAS.NON_EU
}

function getFormFields(values: FormTaxValues) {
  if (isEuArea(values)) {
    return EuFormfields
  }
  if (isNonEuArea(values)) {
    return NonEuFormfields
  }

  return null
}

function handleChangeLegalArea(legalArea: LegalArea) {
  setFormValues(FORM_ID, {
    legalArea,
    isExemptFromVat: false,
  })
}

function validate(
  fieldName: FieldName<FormTaxValues>,
  value: never,
  values: FormTaxValues,
) {
  return makeValidate<FormTaxValues>(getFormValidationRules(i18n, values))(
    fieldName,
    value,
    values,
  ) as unknown as ValidateMethod<FormTaxValues>
}

async function handleSubmit(values: FormTaxValues) {
  isSubmitting.value = true
  try {
    // Upload the OSS document if needed
    if (values.files.length && sellerId) {
      await Promise.all(
        values.ossDocuments.map((document) => deleteLegalDocument(document.id)),
      )
      await uploadLegalDocument(
        sellerId,
        values.files[0],
        OSS_DOCUMENT_TYPE,
        cancelUploadFilePoll,
      )
    }
    await $httpFetch(sellerProfileAPI.putVatInformations, {
      body: cleanFormValues(values),
    })
    emit('update-vat-informations')

    openSuccessToast({
      title: i18n(translations.vatInformationsUpdateSuccessTitle),
      content: i18n(translations.vatInformationsUpdateSuccessMessage),
    })
    closeModal()
  } catch {
    openErrorToast({
      title: i18n(translations.vatInformationsUpdateErrorTitle),
      content: i18n(translations.vatInformationsUpdateErrorMessage),
    })
  } finally {
    isSubmitting.value = false
  }
}

function handleAddInformations() {
  setFormValues(FORM_ID, { hasCompletedVatInfos: true })
  submitForm(FORM_ID)
}

function handleSaveDraft() {
  setFormValues(FORM_ID, { hasCompletedVatInfos: false })
  submitForm(FORM_ID)
}
</script>
